/**
 * Protocol List Component
 *
 * Displays a list of protocols for a company with actions to view, edit, download, finalize and delete.
 * Also supports creating constitutive board meetings linked to annual meetings with board elections.
 */

import { useState, useEffect } from "react";
import {
  FileText,
  Download,
  Trash2,
  Loader2,
  Calendar,
  CheckCircle,
  Clock,
  Plus,
  Edit,
  Lock,
  Eye,
  AlertTriangle,
  Users,
  ListOrdered,
  Send,
  FilePlus,
  ArrowRight,
  Search,
  Filter,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import { DeleteConfirmModal } from "@/components/common/DeleteConfirmModal";
import {
  protocolsApi,
  agendasApi,
  type ProtocolSummary,
  type Protocol,
  type AnnualMeetingWithBoardElection,
  type ConstitutiveBoardDecisions,
  type Agenda,
} from "@/lib/api";
import { formatTime } from "@/lib/utils";
import { ConstitutiveBoardWizard } from "./ConstitutiveBoardWizard";
import { BoardMeetingWizard } from "./BoardMeetingWizard";
import { ExtraordinaryMeetingWizard } from "./ExtraordinaryMeetingWizard";
import { AgendaWizard } from "./AgendaWizard";

interface ProtocolListProps {
  companyId: string;
  onCreateNew?: (agendaId?: string) => void;
  onCreateConstitutive?: () => void; // External trigger to open constitutive wizard
  onEdit?: (protocol: Protocol) => void;
  onCountChange?: (count: number) => void;
}

const PROTOCOL_TYPE_LABELS: Record<string, string> = {
  annual_meeting: "Årsstämma",
  board_meeting: "Styrelsemöte",
  constitutive_board: "Konstituerande styrelsemöte",
  extraordinary_meeting: "Extra bolagsstämma",
};

const MEETING_TYPE_LABELS: Record<string, string> = {
  annual_meeting: "Årsstämma",
  board_meeting: "Styrelsemöte",
  constitutive_board: "Konstituerande styrelsemöte",
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  draft: { label: "Utkast", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  finalized: { label: "Slutfört", color: "bg-green-100 text-green-800", icon: CheckCircle },
};

const AGENDA_STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  draft: { label: "Utkast", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  sent: { label: "Skickad", color: "bg-blue-100 text-blue-800", icon: Send },
  used: { label: "Protokoll skapat", color: "bg-green-100 text-green-800", icon: CheckCircle },
};

export function ProtocolList({ companyId, onCreateNew, onCreateConstitutive: _onCreateConstitutive, onEdit, onCountChange }: ProtocolListProps) {
  const [protocols, setProtocols] = useState<ProtocolSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [finalizingId, setFinalizingId] = useState<string | null>(null);
  const [loadingProtocolId, setLoadingProtocolId] = useState<string | null>(null);

  // Detail modal state
  const [viewingProtocol, setViewingProtocol] = useState<Protocol | null>(null);

  // Finalize confirmation modal
  const [protocolToFinalize, setProtocolToFinalize] = useState<ProtocolSummary | null>(null);

  // Constitutive board wizard state
  const [showConstitutiveWizard, setShowConstitutiveWizard] = useState(false);
  const [annualMeetingsWithElection, setAnnualMeetingsWithElection] = useState<AnnualMeetingWithBoardElection[]>([]);

  // Board meeting wizard state
  const [showBoardMeetingWizard, setShowBoardMeetingWizard] = useState(false);

  // Extraordinary meeting wizard state
  const [showExtraordinaryMeetingWizard, setShowExtraordinaryMeetingWizard] = useState(false);

  // Agenda wizard state
  const [showAgendaWizard, setShowAgendaWizard] = useState(false);
  const [agendas, setAgendas] = useState<Agenda[]>([]);
  const [agendaToEdit, setAgendaToEdit] = useState<Agenda | null>(null);
  const [deletingAgendaId, setDeletingAgendaId] = useState<string | null>(null);

  // Delete confirmation modal state
  const [agendaToDelete, setAgendaToDelete] = useState<Agenda | null>(null);
  const [protocolToDelete, setProtocolToDelete] = useState<ProtocolSummary | null>(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const loadProtocols = async () => {
    setIsLoading(true);
    try {
      const response = await protocolsApi.list(companyId);
      if (response.success && response.data) {
        setProtocols(response.data.protocols);
        onCountChange?.(response.data.protocols.length);
      }
    } catch (error) {
      console.error("Failed to load protocols:", error);
      toast.error("Kunde inte ladda protokoll");
    } finally {
      setIsLoading(false);
    }
  };

  const loadAnnualMeetingsWithElection = async () => {
    try {
      const response = await protocolsApi.getAnnualMeetingsWithBoardElection(companyId);
      if (response.success && response.data) {
        setAnnualMeetingsWithElection(response.data.annualMeetings || []);
      }
    } catch (error) {
      console.error("Failed to load annual meetings:", error);
      setAnnualMeetingsWithElection([]);
    }
  };

  const loadAgendas = async () => {
    try {
      const response = await agendasApi.list(companyId);
      if (response.success && response.data) {
        setAgendas(response.data.agendas || []);
      }
    } catch (error) {
      console.error("Failed to load agendas:", error);
      setAgendas([]);
    }
  };

  useEffect(() => {
    loadProtocols();
    loadAnnualMeetingsWithElection();
    loadAgendas();
  }, [companyId]);

  // Filter protocols based on search query, type, and status
  const filteredProtocols = protocols.filter((protocol) => {
    // Search filter - match against type label, chairperson name, or date
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const typeLabel = (PROTOCOL_TYPE_LABELS[protocol.type] || protocol.type).toLowerCase();
      const chairperson = protocol.chairpersonName.toLowerCase();
      const date = protocol.meetingDate.toLowerCase();
      if (!typeLabel.includes(query) && !chairperson.includes(query) && !date.includes(query)) {
        return false;
      }
    }

    // Type filter
    if (typeFilter && protocol.type !== typeFilter) {
      return false;
    }

    // Status filter
    if (statusFilter && protocol.status !== statusFilter) {
      return false;
    }

    return true;
  });

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setTypeFilter(null);
    setStatusFilter(null);
  };

  const hasActiveFilters = searchQuery || typeFilter || statusFilter;

  const handleConstitutiveSuccess = (_protocolId: string) => {
    loadProtocols();
    loadAnnualMeetingsWithElection();
  };

  const handleBoardMeetingSuccess = (_protocolId: string) => {
    loadProtocols();
    setShowBoardMeetingWizard(false);
    toast.success("Styrelseprotokoll skapat");
  };

  const handleExtraordinaryMeetingSuccess = (_protocolId: string) => {
    loadProtocols();
    setShowExtraordinaryMeetingWizard(false);
    toast.success("Extra bolagsstämmoprotokoll skapat");
  };

  const handleAgendaSuccess = (_agendaId: string) => {
    loadAgendas();
    setAgendaToEdit(null);
    toast.success(agendaToEdit ? "Dagordning uppdaterad" : "Dagordning skapad");
  };

  const handleEditAgenda = (agenda: Agenda) => {
    if (agenda.status === "used") {
      toast.error("Dagordningar med skapat protokoll kan inte redigeras");
      return;
    }
    setAgendaToEdit(agenda);
    setShowAgendaWizard(true);
  };

  const handleDeleteAgenda = async (agenda: Agenda) => {
    // Open confirmation modal
    setAgendaToDelete(agenda);
  };

  const confirmDeleteAgenda = async () => {
    if (!agendaToDelete) return;

    setDeletingAgendaId(agendaToDelete.id);
    try {
      const response = await agendasApi.delete(companyId, agendaToDelete.id);
      if (response.success) {
        toast.success("Dagordning raderad");
        setAgendas((prev) => prev.filter((a) => a.id !== agendaToDelete.id));
        setAgendaToDelete(null);
      } else {
        toast.error("Kunde inte radera dagordningen");
      }
    } catch (error) {
      console.error("Delete agenda failed:", error);
      toast.error("Ett fel uppstod");
    } finally {
      setDeletingAgendaId(null);
    }
  };

  const handleView = async (protocol: ProtocolSummary) => {
    setLoadingProtocolId(protocol.id);
    try {
      const response = await protocolsApi.get(companyId, protocol.id);
      if (response.success && response.data) {
        setViewingProtocol(response.data.protocol);
      } else {
        toast.error("Kunde inte ladda protokollet");
      }
    } catch (error) {
      console.error("Failed to load protocol:", error);
      toast.error("Ett fel uppstod");
    } finally {
      setLoadingProtocolId(null);
    }
  };

  const handleEdit = async (protocol: ProtocolSummary) => {
    if (protocol.status === "finalized") {
      toast.error("Slutförda protokoll kan inte redigeras");
      return;
    }

    setLoadingProtocolId(protocol.id);
    try {
      const response = await protocolsApi.get(companyId, protocol.id);
      if (response.success && response.data && onEdit) {
        onEdit(response.data.protocol);
      } else {
        toast.error("Kunde inte ladda protokollet");
      }
    } catch (error) {
      console.error("Failed to load protocol:", error);
      toast.error("Ett fel uppstod");
    } finally {
      setLoadingProtocolId(null);
    }
  };

  const handleDownload = async (protocol: ProtocolSummary) => {
    setDownloadingId(protocol.id);
    try {
      const result = await protocolsApi.downloadPdf(companyId, protocol.id);
      if (result.success && result.blob) {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = result.filename || "protokoll.pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("PDF nedladdad!");
      } else {
        toast.error(result.error || "Kunde inte ladda ner PDF");
      }
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Ett fel uppstod vid nedladdning");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleFinalize = async () => {
    if (!protocolToFinalize) return;

    setFinalizingId(protocolToFinalize.id);
    try {
      const response = await protocolsApi.finalize(companyId, protocolToFinalize.id);
      if (response.success) {
        toast.success("Protokoll slutfört!");
        // Update status in local state
        setProtocols((prev) =>
          prev.map((p) =>
            p.id === protocolToFinalize.id ? { ...p, status: "finalized" as const } : p
          )
        );
        setProtocolToFinalize(null);
      } else {
        toast.error(response.error?.message || "Kunde inte slutföra protokoll");
      }
    } catch (error) {
      console.error("Finalize failed:", error);
      toast.error("Ett fel uppstod");
    } finally {
      setFinalizingId(null);
    }
  };

  const handleDelete = async (protocol: ProtocolSummary) => {
    if (protocol.status === "finalized") {
      toast.error("Slutförda protokoll kan inte raderas");
      return;
    }

    // Open confirmation modal
    setProtocolToDelete(protocol);
  };

  const confirmDeleteProtocol = async () => {
    if (!protocolToDelete) return;

    setDeletingId(protocolToDelete.id);
    try {
      const response = await protocolsApi.delete(companyId, protocolToDelete.id);
      if (response.success) {
        toast.success("Protokoll raderat");
        setProtocols((prev) => {
          const updated = prev.filter((p) => p.id !== protocolToDelete.id);
          onCountChange?.(updated.length);
          return updated;
        });
        setProtocolToDelete(null);
      } else {
        toast.error(response.error?.message || "Kunde inte radera protokoll");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Ett fel uppstod");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  // Show main content even if no protocols - we need to show agendas
  const hasNoContent = protocols.length === 0 && agendas.length === 0 && annualMeetingsWithElection.length === 0;

  if (hasNoContent) {
    return (
      <>
        <div className="text-center py-8">
          <ListOrdered className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Inga dagordningar eller protokoll</h3>
          <p className="text-sm text-gray-500 mb-4">
            Börja med att skapa en dagordning för årsstämman. När dagordningen är skickad kan du skapa protokollet.
          </p>
          <button
            onClick={() => setShowAgendaWizard(true)}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Skapa dagordning
          </button>
        </div>

        {/* Constitutive Board Wizard */}
        <ConstitutiveBoardWizard
          isOpen={showConstitutiveWizard}
          onClose={() => setShowConstitutiveWizard(false)}
          companyId={companyId}
          onSuccess={handleConstitutiveSuccess}
        />

        {/* Board Meeting Wizard */}
        <BoardMeetingWizard
          isOpen={showBoardMeetingWizard}
          onClose={() => setShowBoardMeetingWizard(false)}
          companyId={companyId}
          onSuccess={handleBoardMeetingSuccess}
        />

        {/* Extraordinary Meeting Wizard */}
        <ExtraordinaryMeetingWizard
          isOpen={showExtraordinaryMeetingWizard}
          onClose={() => setShowExtraordinaryMeetingWizard(false)}
          companyId={companyId}
          onSuccess={handleExtraordinaryMeetingSuccess}
        />

        {/* Agenda Wizard */}
        <AgendaWizard
          isOpen={showAgendaWizard}
          onClose={() => {
            setShowAgendaWizard(false);
            setAgendaToEdit(null);
          }}
          companyId={companyId}
          meetingType="annual_meeting"
          existingAgenda={agendaToEdit}
          onSuccess={handleAgendaSuccess}
        />
      </>
    );
  }

  return (
    <>
      {/* Call to action for constitutive board meeting */}
      {annualMeetingsWithElection.length > 0 && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-900">Konstituerande styrelsemöte</h4>
              <p className="text-sm text-blue-700 mt-1">
                {annualMeetingsWithElection.length === 1
                  ? "Det finns en årsstämma med styrelseval som saknar konstituerande protokoll."
                  : `Det finns ${annualMeetingsWithElection.length} årsstämmor med styrelseval som saknar konstituerande protokoll.`}
              </p>
              <button
                onClick={() => setShowConstitutiveWizard(true)}
                className="mt-2 btn btn-sm bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Skapa konstituerande protokoll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Button for creating board meeting protocol */}
      <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-purple-900">Styrelseprotokoll</h4>
            <p className="text-sm text-purple-700 mt-1">
              Skapa protokoll för ordinarie styrelsemöten. Protokollen numreras automatiskt enligt ABL 8 kap 24§.
            </p>
            <button
              onClick={() => setShowBoardMeetingWizard(true)}
              className="mt-2 btn btn-sm bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Skapa styrelseprotokoll
            </button>
          </div>
        </div>
      </div>

      {/* Button for creating extraordinary meeting protocol */}
      <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-orange-900">Extra bolagsstämma</h4>
            <p className="text-sm text-orange-700 mt-1">
              Skapa protokoll för extra bolagsstämma enligt ABL 7 kap 13-14§. Används vid kapitaländring, bolagsordningsändring, styrelseändring eller på begäran av aktieägare.
            </p>
            <button
              onClick={() => setShowExtraordinaryMeetingWizard(true)}
              className="mt-2 btn btn-sm bg-orange-600 text-white hover:bg-orange-700 inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Skapa extra stämmoprotokoll
            </button>
          </div>
        </div>
      </div>

      {/* Agendas Section */}
      {agendas.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Dagordningar</h3>
            <button
              onClick={() => setShowAgendaWizard(true)}
              className="btn btn-sm btn-secondary inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              Ny dagordning
            </button>
          </div>
          <div className="space-y-2">
            {agendas.map((agenda) => {
              const statusConfig = AGENDA_STATUS_CONFIG[agenda.status] || AGENDA_STATUS_CONFIG.draft;
              const StatusIcon = statusConfig.icon;
              const canCreateProtocol = agenda.status === "sent";

              return (
                <div
                  key={agenda.id}
                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                      <ListOrdered className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {MEETING_TYPE_LABELS[agenda.meetingType] || agenda.meetingType}
                        </h4>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        {new Date(agenda.meetingDate).toLocaleDateString("sv-SE", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {canCreateProtocol && (
                      <button
                        onClick={() => {
                          if (onCreateNew) onCreateNew(agenda.id);
                        }}
                        className="btn btn-sm btn-primary inline-flex items-center gap-1.5"
                        title="Skapa protokoll från denna dagordning"
                      >
                        <FilePlus className="w-4 h-4" />
                        Skapa protokoll
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                    {/* Edit button - available for draft and sent agendas */}
                    {agenda.status !== "used" && (
                      <button
                        onClick={() => handleEditAgenda(agenda)}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title="Redigera dagordning"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                    {/* Delete button - for all agendas */}
                    <button
                      onClick={() => handleDeleteAgenda(agenda)}
                      disabled={deletingAgendaId === agenda.id}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Radera dagordning"
                    >
                      {deletingAgendaId === agenda.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA for creating first agenda (when no agendas exist but protocols do) */}
      {agendas.length === 0 && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <ListOrdered className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-amber-900">Dagordning för årsstämma</h4>
              <p className="text-sm text-amber-700 mt-1">
                Skapa och skicka ut dagordning inför årsstämman via e-post till aktieägare och styrelseledamöter.
                När dagordningen är skickad kan du skapa protokollet.
              </p>
              <button
                onClick={() => setShowAgendaWizard(true)}
                className="mt-2 btn btn-sm bg-amber-600 text-white hover:bg-amber-700 inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Skapa dagordning
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Protocols Section */}
      {protocols.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Protokoll ({filteredProtocols.length} av {protocols.length})
            </h3>
          </div>

          {/* Search and Filter Bar */}
          {protocols.length > 2 && (
            <div className="mb-4 flex flex-wrap items-center gap-3">
              {/* Search Input */}
              <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Sök protokoll..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Type Filter */}
              <div className="relative">
                <select
                  value={typeFilter || ""}
                  onChange={(e) => setTypeFilter(e.target.value || null)}
                  className="appearance-none pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  <option value="">Alla typer</option>
                  <option value="annual_meeting">Årsstämma</option>
                  <option value="board_meeting">Styrelsemöte</option>
                  <option value="constitutive_board">Konstituerande</option>
                  <option value="extraordinary_meeting">Extra stämma</option>
                </select>
                <Filter className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <select
                  value={statusFilter || ""}
                  onChange={(e) => setStatusFilter(e.target.value || null)}
                  className="appearance-none pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  <option value="">Alla status</option>
                  <option value="draft">Utkast</option>
                  <option value="finalized">Slutfört</option>
                </select>
                <Filter className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Rensa filter
                </button>
              )}
            </div>
          )}
        </>
      )}

      <div className="space-y-3">
        {filteredProtocols.length === 0 && hasActiveFilters ? (
          <div className="text-center py-8 text-gray-500">
            <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Inga protokoll matchar dina filter</p>
            <button
              onClick={clearFilters}
              className="mt-2 text-sm text-primary-600 hover:text-primary-700"
            >
              Rensa filter
            </button>
          </div>
        ) : (
          filteredProtocols.map((protocol) => {
            const statusConfig = STATUS_CONFIG[protocol.status] || STATUS_CONFIG.draft;
            const StatusIcon = statusConfig.icon;
            const isLoadingThis = loadingProtocolId === protocol.id;

            return (
              <div
                key={protocol.id}
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
              >
              <div
                className="flex items-center gap-4 flex-1 cursor-pointer"
                onClick={() => handleView(protocol)}
              >
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  {isLoadingThis ? (
                    <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
                  ) : (
                    <FileText className="w-5 h-5 text-primary-600" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900">
                      {PROTOCOL_TYPE_LABELS[protocol.type] || protocol.type}
                    </h4>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}
                    >
                      <StatusIcon className="w-3 h-3" />
                      {statusConfig.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(protocol.meetingDate)}
                    <span className="text-gray-300">|</span>
                    Ordförande: {protocol.chairpersonName}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1">
                {/* View button */}
                <button
                  onClick={() => handleView(protocol)}
                  disabled={isLoadingThis}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Visa detaljer"
                >
                  <Eye className="w-4 h-4" />
                </button>

                {/* Edit button (only for drafts) */}
                {protocol.status === "draft" && onEdit && (
                  <button
                    onClick={() => handleEdit(protocol)}
                    disabled={isLoadingThis}
                    className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Redigera"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                )}

                {/* Download button */}
                <button
                  onClick={() => handleDownload(protocol)}
                  disabled={downloadingId === protocol.id}
                  className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Ladda ner PDF"
                >
                  {downloadingId === protocol.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </button>

                {/* Finalize button (only for drafts) */}
                {protocol.status === "draft" && (
                  <button
                    onClick={() => setProtocolToFinalize(protocol)}
                    disabled={finalizingId === protocol.id}
                    className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Slutför protokoll"
                  >
                    {finalizingId === protocol.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Lock className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Delete button (only for drafts) */}
                {protocol.status === "draft" && (
                  <button
                    onClick={() => handleDelete(protocol)}
                    disabled={deletingId === protocol.id}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Radera"
                  >
                    {deletingId === protocol.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          );
          })
        )}
      </div>

      {/* Finalize Confirmation Modal */}
      <Modal
        isOpen={!!protocolToFinalize}
        onClose={() => setProtocolToFinalize(null)}
        title={
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
            <span>Slutför protokoll</span>
          </div>
        }
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Är du säker på att du vill slutföra detta protokoll?
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>OBS!</strong> När ett protokoll är slutfört kan det inte längre redigeras eller
              raderas. Denna åtgärd går inte att ångra.
            </p>
          </div>
          {protocolToFinalize && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <strong>Protokoll:</strong> {PROTOCOL_TYPE_LABELS[protocolToFinalize.type]}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Datum:</strong> {formatDate(protocolToFinalize.meetingDate)}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Ordförande:</strong> {protocolToFinalize.chairpersonName}
              </p>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => setProtocolToFinalize(null)}
              className="btn btn-secondary"
              disabled={!!finalizingId}
            >
              Avbryt
            </button>
            <button
              onClick={handleFinalize}
              className="btn btn-primary bg-green-600 hover:bg-green-700"
              disabled={!!finalizingId}
            >
              {finalizingId ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Slutför...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Slutför protokoll
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Protocol Detail Modal */}
      <ProtocolDetailModal
        protocol={viewingProtocol}
        onClose={() => setViewingProtocol(null)}
        onDownload={() => viewingProtocol && handleDownload(viewingProtocol as ProtocolSummary)}
        isDownloading={viewingProtocol ? downloadingId === viewingProtocol.id : false}
      />

      {/* Constitutive Board Wizard */}
      <ConstitutiveBoardWizard
        isOpen={showConstitutiveWizard}
        onClose={() => setShowConstitutiveWizard(false)}
        companyId={companyId}
        onSuccess={handleConstitutiveSuccess}
      />

      {/* Board Meeting Wizard */}
      <BoardMeetingWizard
        isOpen={showBoardMeetingWizard}
        onClose={() => setShowBoardMeetingWizard(false)}
        companyId={companyId}
        onSuccess={handleBoardMeetingSuccess}
      />

      {/* Extraordinary Meeting Wizard */}
      <ExtraordinaryMeetingWizard
        isOpen={showExtraordinaryMeetingWizard}
        onClose={() => setShowExtraordinaryMeetingWizard(false)}
        companyId={companyId}
        onSuccess={handleExtraordinaryMeetingSuccess}
      />

      {/* Agenda Wizard */}
      <AgendaWizard
        isOpen={showAgendaWizard}
        onClose={() => {
          setShowAgendaWizard(false);
          setAgendaToEdit(null);
        }}
        companyId={companyId}
        meetingType="annual_meeting"
        existingAgenda={agendaToEdit}
        onSuccess={handleAgendaSuccess}
      />

      {/* Delete Agenda Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!agendaToDelete}
        onClose={() => setAgendaToDelete(null)}
        onConfirm={confirmDeleteAgenda}
        title="Radera dagordning?"
        description={
          agendaToDelete && (
            <>
              Du håller på att radera dagordningen för{" "}
              <strong>{MEETING_TYPE_LABELS[agendaToDelete.meetingType] || agendaToDelete.meetingType}</strong>
              {" "}({new Date(agendaToDelete.meetingDate).toLocaleDateString("sv-SE")}).
              {agendaToDelete.status === "used" && (
                <>
                  <br /><br />
                  <strong className="text-amber-600">OBS!</strong> Ett protokoll har skapats från denna dagordning.
                  Protokollet kommer inte att raderas, men kopplingen till dagordningen tas bort.
                </>
              )}
            </>
          )
        }
        warning="Denna åtgärd går inte att ångra."
        isDeleting={!!deletingAgendaId}
      />

      {/* Delete Protocol Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!protocolToDelete}
        onClose={() => setProtocolToDelete(null)}
        onConfirm={confirmDeleteProtocol}
        title="Radera protokoll?"
        description={
          protocolToDelete && (
            <>
              Du håller på att radera protokollet för{" "}
              <strong>{PROTOCOL_TYPE_LABELS[protocolToDelete.type] || protocolToDelete.type}</strong>
              {" "}({formatDate(protocolToDelete.meetingDate)}).
            </>
          )
        }
        warning="Denna åtgärd går inte att ångra."
        isDeleting={!!deletingId}
      />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Protocol Detail Modal
// ═══════════════════════════════════════════════════════════════════

interface ProtocolDetailModalProps {
  protocol: Protocol | null;
  onClose: () => void;
  onDownload: () => void;
  isDownloading: boolean;
}

function ProtocolDetailModal({
  protocol,
  onClose,
  onDownload,
  isDownloading,
}: ProtocolDetailModalProps) {
  if (!protocol) return null;

  const statusConfig = STATUS_CONFIG[protocol.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const isConstitutiveBoard = protocol.type === "constitutive_board";

  const attendees = (protocol.attendees || []) as Array<{
    name: string;
    shares: number;
    votes: number;
    present: boolean;
    representedBy?: string;
  }>;

  const decisions = (protocol.decisions || {}) as {
    financialStatementsApproved?: boolean;
    profitDisposition?: {
      retainedEarnings: number;
      currentYearResult: number;
      dividend: number;
      carriedForward: number;
    };
    discharge?: Array<{ name: string; role: string; granted: boolean }>;
    boardElection?: { performed: boolean; members: Array<{ name: string; role: string }> };
    auditorElection?: { performed: boolean; auditor: string; firm?: string };
  };

  const constitutiveDecisions = (protocol.decisions || {}) as ConstitutiveBoardDecisions;

  const getSignatureRightsText = (): string => {
    if (!constitutiveDecisions.signatureRights) return "";
    switch (constitutiveDecisions.signatureRights.type) {
      case "board_together":
        return "Styrelsen i förening";
      case "single":
        return `${constitutiveDecisions.signatureRights.singlePerson} ensam`;
      case "two_together":
        return `${constitutiveDecisions.signatureRights.twoPeople?.person1} och ${constitutiveDecisions.signatureRights.twoPeople?.person2} i förening`;
      case "custom":
        return constitutiveDecisions.signatureRights.customText || "";
      default:
        return "";
    }
  };

  return (
    <Modal
      isOpen={!!protocol}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-primary-600" />
          <span>{PROTOCOL_TYPE_LABELS[protocol.type] || protocol.type}</span>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}
          >
            <StatusIcon className="w-3 h-3" />
            {statusConfig.label}
          </span>
        </div>
      }
      size="2xl"
    >
      <div className="space-y-6">
        {/* Meeting Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-1">Datum</h4>
            <p className="text-gray-900">{formatDate(protocol.meetingDate)}</p>
          </div>
          {protocol.meetingTime && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Tid</h4>
              <p className="text-gray-900">{formatTime(protocol.meetingTime)}</p>
            </div>
          )}
          {protocol.meetingPlace && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Plats</h4>
              <p className="text-gray-900">{protocol.meetingPlace}</p>
            </div>
          )}
        </div>

        {/* Officers */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Funktionärer</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Ordförande:</span>{" "}
              <span className="text-gray-900">{protocol.chairpersonName}</span>
            </div>
            {protocol.secretaryName && (
              <div>
                <span className="text-gray-500">Sekreterare:</span>{" "}
                <span className="text-gray-900">{protocol.secretaryName}</span>
              </div>
            )}
            {protocol.adjusterName && (
              <div>
                <span className="text-gray-500">Justerare:</span>{" "}
                <span className="text-gray-900">{protocol.adjusterName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Attendees */}
        {attendees.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              {isConstitutiveBoard ? "Närvarande styrelseledamöter" : "Närvarande"}
            </h4>
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              {isConstitutiveBoard ? (
                // Simpler list for constitutive board meetings (no shares/votes)
                <div className="p-3 space-y-2">
                  {attendees
                    .filter((a) => a.present)
                    .map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-gray-900">{a.name}</span>
                      </div>
                    ))}
                </div>
              ) : (
                // Full table for annual meetings (with shares/votes)
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left p-3 font-medium text-gray-600">Namn</th>
                      <th className="text-right p-3 font-medium text-gray-600">Aktier</th>
                      <th className="text-right p-3 font-medium text-gray-600">Röster</th>
                      <th className="text-center p-3 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendees.map((a, i) => (
                      <tr key={i} className="border-b border-gray-100 last:border-0">
                        <td className="p-3 text-gray-900">{a.name}</td>
                        <td className="p-3 text-right text-gray-900">
                          {a.shares.toLocaleString("sv-SE")}
                        </td>
                        <td className="p-3 text-right text-gray-900">
                          {a.votes.toLocaleString("sv-SE")}
                        </td>
                        <td className="p-3 text-center">
                          {a.present ? (
                            <span className="text-green-600">Närvarande</span>
                          ) : a.representedBy ? (
                            <span className="text-blue-600">Via ombud</span>
                          ) : (
                            <span className="text-gray-400">Frånvarande</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Decisions */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Beslut</h4>
          <div className="space-y-3">
            {/* Annual Meeting Decisions */}
            {!isConstitutiveBoard && (
              <>
                {decisions.financialStatementsApproved && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Resultat- och balansräkning fastställd</span>
                  </div>
                )}

                {decisions.profitDisposition && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Resultatdisposition</h5>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-500">Balanserat resultat:</div>
                      <div className="text-right">
                        {formatCurrency(decisions.profitDisposition.retainedEarnings)}
                      </div>
                      <div className="text-gray-500">Årets resultat:</div>
                      <div className="text-right">
                        {formatCurrency(decisions.profitDisposition.currentYearResult)}
                      </div>
                      <div className="text-gray-500 font-medium">Utdelning:</div>
                      <div className="text-right font-medium">
                        {formatCurrency(decisions.profitDisposition.dividend)}
                      </div>
                      <div className="text-gray-500">I ny räkning:</div>
                      <div className="text-right">
                        {formatCurrency(decisions.profitDisposition.carriedForward)}
                      </div>
                    </div>
                  </div>
                )}

                {decisions.discharge && decisions.discharge.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-1">Ansvarsfrihet</h5>
                    <div className="text-sm text-gray-600">
                      {decisions.discharge.filter((d) => d.granted).length === decisions.discharge.length
                        ? "Ansvarsfrihet beviljad för samtliga styrelseledamöter"
                        : decisions.discharge.map((d) => (
                            <div key={d.name} className="flex items-center gap-2">
                              {d.granted ? (
                                <CheckCircle className="w-3 h-3 text-green-500" />
                              ) : (
                                <AlertTriangle className="w-3 h-3 text-red-500" />
                              )}
                              <span>
                                {d.name} ({d.role})
                              </span>
                            </div>
                          ))}
                    </div>
                  </div>
                )}

                {decisions.boardElection?.performed && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-1">Styrelse omvald</h5>
                    <div className="text-sm text-gray-600">
                      {decisions.boardElection.members.map((m) => m.name).join(", ")}
                    </div>
                  </div>
                )}

                {decisions.auditorElection?.performed && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-1">Revisor</h5>
                    <div className="text-sm text-gray-600">
                      {decisions.auditorElection.auditor}
                      {decisions.auditorElection.firm && ` (${decisions.auditorElection.firm})`}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Constitutive Board Meeting Decisions */}
            {isConstitutiveBoard && (
              <>
                {constitutiveDecisions.chairpersonElection?.name && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Ordförande: {constitutiveDecisions.chairpersonElection.name}</span>
                  </div>
                )}

                {constitutiveDecisions.signatureRights && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Firmateckning: {getSignatureRightsText()}</span>
                  </div>
                )}

                {constitutiveDecisions.attestRules?.enabled && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Attestregler</h5>
                    <div className="text-sm text-gray-600">
                      <p>
                        Belopp upp till {constitutiveDecisions.attestRules.lowLimit?.toLocaleString("sv-SE")} kr:{" "}
                        {constitutiveDecisions.attestRules.lowAttester}
                      </p>
                      <p>
                        Belopp däröver: {constitutiveDecisions.attestRules.highAttester}
                      </p>
                    </div>
                  </div>
                )}

                {constitutiveDecisions.ceoAppointment?.appointed && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>VD: {constitutiveDecisions.ceoAppointment.name}</span>
                  </div>
                )}

                {constitutiveDecisions.bankAuthorization?.enabled && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>
                      Bankbehörighet: {constitutiveDecisions.bankAuthorization.authorizedPerson}
                      {constitutiveDecisions.bankAuthorization.bank &&
                        ` (${constitutiveDecisions.bankAuthorization.bank})`}
                    </span>
                  </div>
                )}

                {constitutiveDecisions.nextMeeting?.scheduled && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Nästa möte: {constitutiveDecisions.nextMeeting.date}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Other Business */}
        {protocol.otherBusiness && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Övriga ärenden</h4>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{protocol.otherBusiness}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button onClick={onClose} className="btn btn-secondary">
            Stäng
          </button>
          <button onClick={onDownload} disabled={isDownloading} className="btn btn-primary">
            {isDownloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Laddar...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Ladda ner PDF
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ProtocolList;
